import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Label } from "@shared/ui/label";
import { Droplet, Check } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { toast } from "@shared/hooks/use-toast";
import { TableColumn } from "@shared/types/table";
import { useAssignedUnitResources } from "@features/cx/consumer-onboarding/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface AddMeterConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddConnection: (connectionData: any) => void;
  utilityName?: string;
  consumerId?: string;
  remoteUtilityId?: string;
  premiseCode?: string;
  connectionNumber?: number;
}

interface MeterItem {
  id: string;
  resourceId: string;
  meterNumber: string;
  deviceNo: string;
  physicalStatusDisplay: string;
  statusDisplay: string;
  meterType: string;
  model: string;
  lastReading: string;
}

export const AddMeterConnectionModal: React.FC<
  AddMeterConnectionModalProps
> = ({
  isOpen,
  onClose,
  onAddConnection,
  utilityName = "Water",
  consumerId = "",
  remoteUtilityId = "",
  premiseCode = "",
  connectionNumber = 1,
}) => {
    const utilityId = useRemoteUtilityId();
    const [selectedMeter, setSelectedMeter] = useState<MeterItem | null>(null);
    const [latestReading, setLatestReading] = useState("");
    const [readingDate, setReadingDate] = useState("");

    const { data: meterSearchData, isLoading: isSearching } =
      useAssignedUnitResources({
        consumer_id: consumerId,
        remote_utility_id: utilityId || remoteUtilityId,
        utility_service: utilityName,
      });

    const apiMeters: MeterItem[] =
      meterSearchData?.result?.map((m: any) => ({
        id: m.meterId || m.id || "N/A",
        resourceId: m.resourceId || "N/A",
        meterNumber: m.resource_id || m.meterId || m.id || "N/A",
        deviceNo: m.serial_no || m.serialNo || m.deviceNo || "N/A",
        physicalStatusDisplay:
          m.physical_status || m.physicalStatusDisplay || m.physicalStatus || m.status || "N/A",
        statusDisplay:
          m.meter_status || m.statusDisplay || m.status || "N/A",
        meterType: m.resource_type_code || m.meterType || "N/A",
        model: m.resource_sub_type_code || m.model || "N/A",
        lastReading: String(m.last_reading ?? m.lastReading ?? "N/A"),
      })) || [];

    const handleClose = () => {
      setSelectedMeter(null);
      setLatestReading("");
      setReadingDate("");
      onClose();
    };

    const handleAssignSubmit = () => {
      if (!selectedMeter) {
        toast({
          variant: "destructive",
          description: "Please select an available resource.",
        });
        return;
      }

      if (!latestReading || !readingDate) {
        toast({
          variant: "destructive",
          description: "Please enter the latest meter reading and reading date.",
        });
        return;
      }

      const formattedDate = readingDate
        ? readingDate.replace(/-/g, "/")
        : new Date().toISOString().split("T")[0].replace(/-/g, "/");

      const isoDate = readingDate
        ? new Date(readingDate).toISOString()
        : new Date().toISOString();

      const connectionItem = {
        connection_number: connectionNumber,
        action: "assign_existing_meter",
        meter_id: Number(selectedMeter.id) || selectedMeter.id,
        meter_no: selectedMeter.meterNumber || String(selectedMeter.id),
        device_no: selectedMeter.deviceNo,
        current_reading: String(latestReading),
        current_reading_date: isoDate,
      };

      const newConnection = {
        connections: {
          [utilityName]: [connectionItem],
        },
        id: selectedMeter.id,
        deviceNo: selectedMeter.deviceNo,
        meterType: selectedMeter.meterType,
        model: selectedMeter.model,
        assignmentDate: new Date().toISOString().split("T")[0].replace(/-/g, "/"),
        installationDate: formattedDate,
        readingStatus: "Normal",
        lastReading: `${latestReading} (${formattedDate})`,
        readings: [
          {
            date: formattedDate,
            reading: Number(latestReading),
            status: "NORMAL",
          },
        ],
      };

      onAddConnection(newConnection);
      toast({
        description: `Meter assigned and ${utilityName.toLowerCase()} connection added successfully.`,
      });
      handleClose();
    };

    const meterColumns: TableColumn<MeterItem>[] = [
      { key: "resourceId", header: "Resource ID", type: "text" },
      { key: "deviceNo", header: "Serial No.", type: "text" },
      { key: "physicalStatusDisplay", header: "Status", type: "text" },
      { key: "lastReading", header: "Last Read", type: "text" },
      {
        key: "action",
        header: "Action",
        type: "custom",
        render: (_, row: MeterItem) => {
          const isSelected = selectedMeter?.id === row.id;
          return (
            <Button
              type="button"
              size="sm"
              variant={isSelected ? "default" : "outline"}
              className={
                isSelected
                  ? "bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3"
                  : "text-xs h-8 px-3"
              }
              onClick={() => setSelectedMeter(row)}
            >
              {isSelected ? (
                <span className="flex items-center gap-1">
                  <Check className="w-3.5 h-3.5" /> Selected
                </span>
              ) : (
                "Select"
              )}
            </Button>
          );
        },
      },
    ];

    return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="sm:max-w-[620px] flex flex-col p-0 overflow-hidden rounded-2xl bg-white shadow-xl border border-gray-100">
          <DialogHeader className="px-6 py-3.5 border-b border-gray-100 flex flex-row items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Droplet className="w-5 h-5 text-blue-600" />
              <DialogTitle className="text-base font-semibold text-gray-900">
                Add {utilityName} Connection
              </DialogTitle>
              <Badge
                variant="secondary"
                className="bg-emerald-50 text-emerald-700 border border-emerald-300 text-xs font-medium rounded-full px-2.5 py-0.5"
              >
                Meter Based
              </Badge>
            </div>
          </DialogHeader>

          <div className="px-6 py-3.5 space-y-3 flex-1 overflow-y-auto max-h-[75vh]">
            <div className="space-y-3">
              {/* Table Section */}
              <div className="space-y-1.5">
                <h4 className="text-xs font-semibold text-gray-500 tracking-wider">
                  Select Available Resource
                </h4>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <GenericTable
                    data={apiMeters}
                    columns={meterColumns}
                    rowKey="id"
                    loading={isSearching}
                  />
                </div>
              </div>

              {/* Selected Meter Details & Reading inputs */}
              {selectedMeter && (
                <div className="space-y-3 pt-3 border-t border-gray-100">
                  <div className="p-3 bg-blue-50/60 border border-blue-200 rounded-xl space-y-2">
                    <h4 className="text-xs font-semibold text-blue-950">
                      Selected {utilityName} Resource Details
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <p className="text-gray-500 font-medium">Resource No</p>
                        <p className="font-bold text-gray-900">{selectedMeter.meterNumber}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 font-medium">Serial No</p>
                        <p className="font-bold text-gray-900">{selectedMeter.deviceNo}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 font-medium">Status</p>
                        <p className="font-semibold text-gray-800">{selectedMeter.physicalStatusDisplay}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 font-medium mb-0.5">State</p>
                        <Badge variant="outline" className="text-green-800 bg-green-100 text-[10px] px-2 py-0">
                          {selectedMeter.statusDisplay}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="latestReading" className="text-xs font-medium text-gray-700">
                        Latest Meter Reading <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="latestReading"
                        type="number"
                        placeholder="Enter current meter reading"
                        value={latestReading}
                        onChange={(e) => setLatestReading(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="readingDate" className="text-xs font-medium text-gray-700">
                        Reading Date <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="readingDate"
                        type="date"
                        value={readingDate}
                        onChange={(e) => setReadingDate(e.target.value)}
                        className="h-9 text-xs"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Assign Submit Footer */}
              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleAssignSubmit}
                  disabled={!selectedMeter || !latestReading || !readingDate}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                >
                  Add Connection
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

// Export backward compatible alias
export const AddWaterConnectionModal = AddMeterConnectionModal;
